import React from 'react'
import Box from '@mui/material/Box'
import { EmtDrawer } from 'emt-components-v2'
import { lockScroll, unlockScroll } from '@/utils/scrollLock'
import ProfileComponent from '@/components/Profile/ProfileComponent'
import PetSitterProfileComponent from '@/components/Profile/PetSitterProfileComponent'
import PublicPetSitterProfileComponent from '@/components/Profile/PublicPetSitterProfileComponent'
import { ProfileKind } from '@/hooks/useFloatingProfile'
import useCurrentUser from '@/hooks/useCurrentUser'

type Props = {
  isMobile: boolean
  open: boolean
  customerId?: string
  profileKind: ProfileKind | null
  onClose: () => void
}

const FloatingProfileModal = React.memo(function FloatingProfileModal({
  isMobile,
  open,
  customerId,
  profileKind,
  onClose,
}: Props) {
  const { currentUser } = useCurrentUser()
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!isMobile) return
    if (!open) return
    lockScroll()
    return () => unlockScroll()
  }, [isMobile, open])

  if (!isMobile || !customerId) return null

  return (
    <EmtDrawer
      variant="temporary"
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{
        borderRadius: 0,
        padding: 0,
        '& .MuiDrawer-paper': {
          maxHeight: '100dvh',
          overflow: 'hidden',
        },
        '& .drawer-scroll': {
          height: '100%',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        },
      }}
      onTop={false}
      // important si tu gères toi-même le scroll lock (sinon MUI le fait aussi)
      ModalProps={{ disableScrollLock: true }}
    >
      <Box className="drawer-scroll" ref={scrollContainerRef}>
        {profileKind === ProfileKind.Community && (
          <ProfileComponent customerId={customerId} overrideBackButton={onClose} />
        )}

        {profileKind === ProfileKind.PetSitting && currentUser && (
          <PetSitterProfileComponent
            customerId={customerId}
            overrideBackButton={onClose}
            scrollContainerRef={scrollContainerRef}
          />
        )}

        {profileKind === ProfileKind.PetSitting && !currentUser && (
          <PublicPetSitterProfileComponent
            customerId={customerId}
            overrideBackButton={onClose}
            scrollContainerRef={scrollContainerRef}
          />
        )}
      </Box>
    </EmtDrawer>
  )
})

export default FloatingProfileModal
